import { useEffect, useState, useRef } from 'react';
import { supabase } from '../../../../common/src/supabase';
import { Button, Input } from '@nextui-org/react';
import { toast } from 'react-toastify';
import { fetchUserProfile } from '../../../../common/src/api/users';
import { getExternalProfiles } from '../../../../common/src/api/external-profiles';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

const AuthPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>();
  const turnstileRef = useRef<TurnstileInstance>(null);
  const backgrounds = ['/kiwi.jpg', '/orange.jpg', '/cherry.jpg'];
  const [bgIndex, setBgIndex] = useState(0);

  useEffect(() => {
    // Preload images to avoid flicker during transitions
    backgrounds.forEach((src) => {
      const img = new Image();
      img.src = src;
    });

    const id = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % backgrounds.length);
    }, 4000); // rotate every 6 seconds
    return () => clearInterval(id);
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('请输入电子邮件和密码 / Please enter email and password');
      return;
    }

    if (!captchaToken) {
      toast.error('请完成安全验证 / Please complete the security verification');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });

      if (error) {
        toast.error(error.message);
        turnstileRef.current?.reset();
        setCaptchaToken(undefined);
        return;
      }

      if (data.user) {
        const [userProfile, externalProfiles] = await Promise.all([
          fetchUserProfile(data.user.id),
          getExternalProfiles(),
        ]);

        if (
          !userProfile?.importer_id &&
          !userProfile?.roles?.includes('superadmin') &&
          externalProfiles.profiles.length === 0
        ) {
          await supabase.auth.signOut();
          toast.error(
            '您没有访问此平台的权限 / You do not have permission to access this platform'
          );
          turnstileRef.current?.reset();
          setCaptchaToken(undefined);
          return;
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('登录错误 / Login error');
      turnstileRef.current?.reset();
      setCaptchaToken(undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='relative flex h-screen w-screen'>
      {/* Brand Logo */}
      <div className='absolute left-2 top-5 left-4 lg:left-5'>
        <div className='flex items-center'>
          {/* <span className='text-2xl font-bold text-brand-900'>
            FruitMarkets
          </span> */}
          <img
            src='/fruitmarkets/logo-horizontal.png'
            alt='FruitMarkets'
            className='h-6'
          />
        </div>
      </div>

      {/* Login Form */}
      <div className='flex w-full items-center justify-center lg:w-1/2'>
        <div className='w-full max-w-md p-8 bg-content1 rounded-lg shadow-md mx-4'>
          {/* design-ignore: pantalla de login */}
          <h2 className='text-2xl font-semibold mb-6'>登录 / Login</h2>
          <form onSubmit={handleLogin} className='flex flex-col gap-4'>
            <Input
              type='email'
              label='电子邮件 / Email'
              placeholder='your@email.com'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isRequired
            />
            <Input
              type='password'
              label='密码 / Password'
              placeholder='••••••••'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isRequired
            />

            {/* Cloudflare Turnstile */}
            <div className='flex justify-center'>
              <Turnstile
                ref={turnstileRef}
                siteKey='0x4AAAAAACKyDNxQSOYs1Pwy'
                onSuccess={(token) => {
                  setCaptchaToken(token);
                }}
                onError={() => {
                  setCaptchaToken(undefined);
                  toast.error(
                    '安全验证错误，请刷新页面 / Security verification error. Please reload the page.'
                  );
                }}
                onExpire={() => {
                  setCaptchaToken(undefined);
                }}
              />
            </div>

            <Button
              type='submit'
              color='primary'
              className='w-full'
              isLoading={loading}
              isDisabled={!captchaToken}
            >
              登录 / Sign In
            </Button>
          </form>
        </div>
      </div>

      {/* Right side - Rotating Fruit Image with smooth crossfade */}
      <div className='relative hidden w-1/2 flex-col-reverse rounded-medium shadow-small lg:flex overflow-hidden'>
        {backgrounds.map((src, idx) => (
          <div
            key={src}
            className={`absolute inset-0 bg-center bg-cover transition-opacity duration-1000 ease-in-out ${
              bgIndex === idx ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ backgroundImage: `url(${src})` }}
          />
        ))}
        {/* <div className='bg-gradient-to-t from-black to-transparent'>
          <div className='flex flex-col items-end gap-4 p-6 rounded-lg'>
            <div className='flex flex-col items-end'>
              <p className='text-right text-2xl font-bold text-white'>
                FruitMarkets
              </p>
              <p className='text-right text-lg text-white/80'>
                市场数据平台 / Market Data Platform
              </p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default AuthPage;
